import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNotification } from "../../hooks/useNotification";
import { getEmployee, getEmployees } from "../../api/employeeAPI";
import { createNewEmployee, deleteEmployee, editEmployee } from "../../api/employeeAPI";
import { EmployeeData, EmployeeDataCreate, EmployeeDataEdit } from "../../types/api/employee";
import Search from "../../components/Search/Search";
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Pagination,
    Paper,
    Select,
    SelectChangeEvent,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
} from "@mui/material";
import { MODAL_TYPE } from "../../constants/common";
import Modal from "../../components/UI/Modal/Modal";
import CustomSnackbar from "../../components/UI/CustomSnackbar";
import Loader from "../../components/Loader/Loader";
import { MainContext, MainContextState } from "../../context/MainContext";
import EditContent from "./Modals/EditContent";
import CreateContent from "./Modals/CreateContent";
import { BASE_IMG_URL } from "../../constants/mainAPI";
import TabPanel from "../../components/TabPanel/TabPanel";
import { DocumentTable } from "../../components/UI/DocumentTable/DocumentTable";
import { deleteDocument, getDocuments } from "../../api/documentAPI";
import { DocumentData, DocumentType } from "../../types/api/documents";
import { UploadButtonForm } from "../../components/forms/ReportForm/ui/UploadButtonForm";
import { TagData, TagType } from "../../types/api/tag";
import { getTags } from "../../api/tagAPI";
import { LIMIT } from "../../constants/fiasAPI";

const tagIdSelector = (tab: number) => {
    switch (tab) {
        case 2: {
            return 26;
        }
        case 3: {
            return 27;
        }
        default:
            return 26;
    }
};

const EmployeePage = () => {
    const { id } = useParams();
    const isOwner =
        localStorage.getItem("employee_type") == "16" &&
        localStorage.getItem("employee_center_id") == localStorage.getItem("current_center_id");
    const isMe = localStorage.getItem("employee_id");
    const { constants } = useContext<Partial<MainContextState>>(MainContext);

    const [tab, setTab] = useState(1);

    const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTab(newValue);
        if (newValue !== 1) {
            setDocTagId(tagIdSelector(newValue));
        }
    };

    const [load, setLoad] = useState(false);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
    });
    const [employees, setEmployees] = useState<Array<EmployeeData>>();
    const [employeeId, setEmployeeId] = useState(0);
    const [deleteEmployeeId, setDeleteEmployeeId] = useState(0);
    const [query, setQuery] = useState("");
    const { setNotification, setOpenSnackbar, notification, openSnackbar } = useNotification();
    const [showModal, setShowModal] = useState<boolean>(false);
    const [currentModalType, setCurrentModalType] = useState("edit");
    const closeModal = () => {
        setShowModal(false);
    };

    const [documents, setDocuments] = useState<DocumentData[]>([]);
    const [deleteDocumentId, setDeleteDocumentId] = useState(0);
    const [docTagId, setDocTagId] = useState(0);

    const [tags, setTags] = useState<Array<TagData>>();

    const fetchTagData = () => {
        setLoad(true);
        getTags(
            result => {
                setTags(result.data);
                setLoad(false);
            },
            () => {
                setLoad(false);
            },
            {
                type: TagType.EMPLOYEE_WORK_INFO,
                [LIMIT]: -1,
            },
        );
    };

    const [type, setType] = useState("16");

    const fetchEmployees = (query?: string) => {
        setLoad(true);
        getEmployees(
            id,
            ({ data, last_page, current_page }) => {
                setEmployees(data);
                setPagination({ ...pagination, last_page: last_page, current_page: current_page });
                setLoad(false);
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
                setLoad(false);
            },
            query
                ? { page: pagination.current_page, q: query, type: type }
                : { page: pagination.current_page, type: type },
        );
    };

    const fetchDocuments = () => {
        setLoad(true);
        getDocuments(
            id,
            { format: DocumentType.DOCUMENT, tag_id: docTagId, limit: -1 },
            ({ data, last_page, current_page }) => {
                setDocuments(data);
                setLoad(false);
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
                setLoad(false);
            },
        );
    };

    const editResetFormData = (data: EmployeeData) => {
        editForm.reset({
            ...data,
            employee_type: Number(data.employee_data.employee_type),
            from: data.employee_data?.from?.slice(0, 10).replace(/-/g, "/"),
            to: data.employee_data?.to?.slice(0, 10).replace(/-/g, "/"),
            work_position: data.employee_data.work_position || [],
            work_info: data.employee_data.work_info,
            photos: [data.photo],
        });
    };

    const getEmployeeData = (employeeId: number) => {
        setLoad(true);
        getEmployee(
            id,
            employeeId,
            ({ data }) => {
                setLoad(false);
                setShowModal(true);
                editResetFormData(data);
            },
            err => {
                setLoad(false);
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
            },
        );
    };

    const createForm = useForm<EmployeeDataCreate>({
        defaultValues: {
            work_position: [" "],
        },
    });
    const editForm = useForm<EmployeeDataEdit>();

    const create: SubmitHandler<EmployeeDataCreate> = (data: EmployeeDataCreate) => {
        setLoad(true);
        createNewEmployee(
            id,
            { ...data, employee_type: Number(data.employee_type) },
            () => {
                fetchEmployees(query);
                setNotification({
                    severity: "success",
                    message: "Данные о работнике добавлены успешно",
                });
                setOpenSnackbar(true);
                setLoad(false);
                closeModal();
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
                setOpenSnackbar(true);
                setLoad(false);
                Object.keys(err.errors).forEach((fieldName: any) => {
                    createForm.setError(fieldName, {
                        type: "custom",
                        message: err.errors[fieldName][0],
                    });
                });
            },
        );
    };
    const edit: SubmitHandler<EmployeeDataEdit> = (data: any) => {
        setLoad(true);
        editEmployee(
            id,
            employeeId,
            data,
            ({ data }) => {
                editResetFormData(data);
                fetchEmployees(query);
                setNotification({
                    severity: "success",
                    message: "Данные работника изменены успешно",
                });
                setOpenSnackbar(true);
                setLoad(false);
                closeModal();
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
                setLoad(false);
                setOpenSnackbar(true);
                Object.keys(err.errors).forEach((fieldName: any) => {
                    editForm.setError(fieldName, {
                        type: "custom",
                        message: err.errors[fieldName][0],
                    });
                });
            },
        );
    };
    const del: SubmitHandler<any> = () => {
        setLoad(true);
        deleteEmployee(
            id,
            deleteEmployeeId,
            () => {
                fetchEmployees(query);
                setNotification({
                    severity: "success",
                    message: "Данные о работнике удалены успешно",
                });
                setOpenSnackbar(true);
                setLoad(false);
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
                setOpenSnackbar(true);
                setLoad(false);
            },
        );
        closeModal();
    };

    const delDoc: SubmitHandler<any> = () => {
        setLoad(true);
        deleteDocument(
            id,
            deleteDocumentId,
            () => {
                fetchDocuments();
                setNotification({
                    severity: "success",
                    message: "Данные о документе удалены успешно",
                });
                setOpenSnackbar(true);
                setLoad(false);
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
                setOpenSnackbar(true);
                setLoad(false);
            },
        );
        closeModal();
    };

    const deleteDocContent = <div>Вы уверены, что хотите удалить данные о документе?</div>;

    const deleteContent = <div>Вы уверены, что хотите удалить данные о оборудовании?</div>;

    const modalType = {
        [MODAL_TYPE.create]: {
            title: "Добавление работника",
            onSubmit: createForm.handleSubmit(create),
            content: <EditContent form={createForm} tags={tags ?? []} />,
        },
        [MODAL_TYPE.edit]: {
            title: "Редактирование данных о работнике",
            onSubmit: editForm.handleSubmit(edit),
            content: <EditContent form={editForm} tags={tags ?? []} />,
        },
        [MODAL_TYPE.delete]: {
            title: "Удаление данных о работнике",
            onSubmit: del,
            content: deleteContent,
        },
        doc_delete: {
            title: "Удаление данных о документе",
            onSubmit: delDoc,
            content: deleteDocContent,
        },
    };

    useEffect(() => {
        setLoad(true);
        fetchEmployees(query);
    }, [pagination.current_page, type, query]);

    useEffect(() => {
        employeeId && getEmployeeData(employeeId);
    }, [employeeId]);

    useEffect(() => {
        if (tab !== 1) {
            fetchDocuments();
        }
    }, [tab]);

    useEffect(() => {
        fetchTagData();
    }, []);

    return (
        <div className={"container"}>
            <div className={"pageHeader"}>Штатное расписание</div>
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }}>
                <Tabs value={tab} onChange={handleChange} aria-label="basic tabs example">
                    <Tab label="Главная" value={1} />
                    <Tab label="Штатное расписание" value={2} />
                    <Tab label="Должностные инструкции" value={3} />
                </Tabs>
            </Box>
            <TabPanel index={1} value={tab}>
                <Stack direction="column" gap={"10px"}>
                    <div style={{ display: "flex", gridGap: "10px" }}>
                        <FormControl
                            sx={{
                                marginTop: "16px",
                                marginBottom: "8px",
                                width: "360px",
                            }}>
                            <InputLabel id={"resident-type-label"}>Тип сотрудника</InputLabel>
                            <Select
                                size={"small"}
                                labelId={"employee-type"}
                                label={"Тип сотрудника"}
                                onChange={(event: SelectChangeEvent) => {
                                    setType(event.target.value);
                                }}
                                value={type}>
                                {constants?.employee_type.map(type => (
                                    <MenuItem key={type.value} value={type.value}>
                                        {type.text}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Search
                            clearHandler={value => {
                                setQuery(value);
                            }}
                            searchHandler={value => {
                                setQuery(value);
                            }}
                        />
                    </div>
                    <TableContainer component={Paper} sx={{ marginBottom: "80px" }}>
                        <Table
                            aria-label="employee table"
                            sx={{
                                "& td": { border: "none" },
                                "& img": {
                                    height: "150px",
                                    width: "150px",
                                    objectFit: "cover",
                                },
                            }}>
                            <TableHead sx={{ "& th": { fontSize: "16px", fontWeight: 700 } }}>
                                <TableRow>
                                    <TableCell>ФИО</TableCell>
                                    <TableCell align="center">Фото</TableCell>
                                    <TableCell align="center">Должности</TableCell>
                                    <TableCell align="center">Тип сотрудника</TableCell>
                                    <TableCell align="center">Телефон</TableCell>
                                    <TableCell align="center">Дата начала</TableCell>
                                    <TableCell align="center">Дата окончания сотрудничества</TableCell>
                                    <TableCell align="center"></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {employees?.map(employee => (
                                    <TableRow key={employee.id} sx={{ border: 0 }}>
                                        <TableCell sx={{ maxWidth: "250px", wordWrap: "break-word" }}>
                                            {[employee.surname, employee.name, employee.lastname].map(
                                                (str, index) =>
                                                    str && (
                                                        <span key={index}>
                                                            {str}
                                                            <br />
                                                        </span>
                                                    ),
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            {employee.photo && (
                                                <img
                                                    alt={employee.photo?.name}
                                                    src={`${BASE_IMG_URL}/${employee.photo?.path}`}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            {(Array.isArray(employee.employee_data?.work_position)
                                                ? employee.employee_data?.work_position
                                                : [""]
                                            ).map((position, index) => (
                                                <span key={index}>
                                                    {position}
                                                    <br />
                                                </span>
                                            ))}
                                        </TableCell>
                                        <TableCell align="center">
                                            {
                                                constants?.employee_type.find(
                                                    state =>
                                                        state.value === Number(employee.employee_data?.employee_type),
                                                )?.text
                                            }
                                        </TableCell>
                                        <TableCell align="center">{employee.phone}</TableCell>
                                        <TableCell align="center">
                                            {String(employee.employee_data?.from?.slice(0, 10) || "")}
                                        </TableCell>
                                        <TableCell align="center">
                                            {String(employee.employee_data?.to?.slice(0, 10) || "")}
                                        </TableCell>
                                        <TableCell align="center">
                                            {isOwner && (
                                                <div className={"btnContainer"}>
                                                    <Button
                                                        variant="outlined"
                                                        fullWidth
                                                        onClick={() => {
                                                            employeeId === employee.id && setShowModal(true);
                                                            setEmployeeId(employee.id);
                                                            setCurrentModalType(MODAL_TYPE.edit);
                                                        }}>
                                                        Изменить
                                                    </Button>
                                                    {Number(isMe) !== employee.id && (
                                                        <Button
                                                            fullWidth
                                                            variant="outlined"
                                                            onClick={() => {
                                                                setShowModal(true);
                                                                setDeleteEmployeeId(employee.id);
                                                                setCurrentModalType(MODAL_TYPE.delete);
                                                            }}>
                                                            Удалить
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <div className={"posBR"}>
                        <div className={"paginationContainer"}>
                            <Pagination
                                onChange={(index, qwerty) => setPagination({ ...pagination, current_page: qwerty })}
                                page={pagination.current_page}
                                count={pagination.last_page}
                                shape="rounded"
                            />
                        </div>
                        {isOwner && (
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    createForm.reset();
                                    setCurrentModalType(MODAL_TYPE.create);
                                    setShowModal(true);
                                }}>
                                Добавить
                            </Button>
                        )}
                    </div>
                </Stack>
            </TabPanel>
            <TabPanel index={2} value={tab}>
                <DocumentTable
                    documents={documents}
                    title={""}
                    onDeleteChange={id => {
                        setShowModal(true);
                        setDeleteDocumentId(id);
                        setCurrentModalType("doc_delete");
                    }}
                />
                <div className={"posBR"}>
                    {isOwner && (
                        <UploadButtonForm
                            onChange={val => {
                                setTimeout(() => {
                                    if (val) {
                                        fetchDocuments();
                                    }
                                }, 300);
                            }}
                            tagId={docTagId}
                        />
                    )}
                </div>
            </TabPanel>
            <TabPanel index={3} value={tab}>
                <DocumentTable
                    documents={documents}
                    title={""}
                    onDeleteChange={id => {
                        setShowModal(true);
                        setDeleteDocumentId(id);
                        setCurrentModalType("doc_delete");
                    }}
                />
                <div className={"posBR"}>
                    {isOwner && (
                        <UploadButtonForm
                            onChange={val => {
                                setTimeout(() => {
                                    if (val) {
                                        fetchDocuments();
                                    }
                                }, 300);
                            }}
                            tagId={docTagId}
                        />
                    )}
                </div>
            </TabPanel>
            <Modal
                open={showModal}
                title={modalType[currentModalType]?.title}
                onClose={() => {
                    closeModal();
                }}
                onSubmit={modalType[currentModalType]?.onSubmit}>
                {modalType[currentModalType]?.content}
            </Modal>
            <CustomSnackbar openSnackbar={openSnackbar} setOpenSnackbar={setOpenSnackbar} notification={notification} />
            <Loader backDrop open={load} />
        </div>
    );
};

export default EmployeePage;
